import React, { useState, useCallback, useMemo } from "react";
import { useT } from "@transifex/react";
import { parse, format, isValid, isAfter, isBefore, startOfDay } from "date-fns";
import { RendererProps } from "./index";
import { QuestionData } from "../../../context/questionnaire/types";
import { TextInput } from "./renderer-styles";

const DEFAULT_MIN_DATE = new Date(1900, 0, 1);

function resolveDate(bound: string | undefined): Date | null {
  if (!bound) return null;
  if (bound === "today") return startOfDay(new Date());
  const d = parse(bound, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : null;
}

interface DateBounds {
  min: Date | null;
  max: Date | null;
}

function getDateBounds(question: QuestionData): DateBounds {
  if (question.min_date || question.max_date) {
    return { min: resolveDate(question.min_date), max: resolveDate(question.max_date) };
  }
  if (question.type === "future_date") {
    return { min: startOfDay(new Date()), max: null };
  }
  if (question.name === "date_of_birth") {
    return { min: DEFAULT_MIN_DATE, max: new Date() };
  }
  return { min: null, max: null };
}

function isDateInRange(date: Date, bounds: DateBounds): boolean {
  if (bounds.min && !isAfter(date, bounds.min) && date.getTime() !== bounds.min.getTime()) {
    return false;
  }
  if (bounds.max && !isBefore(date, bounds.max) && date.getTime() !== bounds.max.getTime()) {
    return false;
  }
  return true;
}

function parseDisplay(input: string, isMonthDate: boolean): Date | null {
  const fmt = isMonthDate ? "MM/yyyy" : "MM/dd/yyyy";
  const parsed = parse(input, fmt, new Date());
  return isValid(parsed) ? parsed : null;
}

function toISO(date: Date, isMonthDate: boolean): string {
  return format(date, isMonthDate ? "yyyy-MM" : "yyyy-MM-dd");
}

const DateInputRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {
  const t = useT();
  const isMonthDate = question.type === "month_date_input";
  const maxLen = isMonthDate ? 7 : 10;
  const digitLimit = isMonthDate ? 6 : 8;
  const bounds = useMemo(() => getDateBounds(question), [question]);

  const [display, setDisplay] = useState<string>(() => {
    const raw = answer?.value ?? "";
    if (!raw) return "";
    const isoFmt = isMonthDate ? "yyyy-MM" : "yyyy-MM-dd";
    const displayFmt = isMonthDate ? "MM/yyyy" : "MM/dd/yyyy";
    const parsed = parse(raw, isoFmt, new Date());
    return isValid(parsed) ? format(parsed, displayFmt) : raw;
  });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      if (input.endsWith("/")) {
        const parts = input.slice(0, -1).split("/");
        const lastPart = parts[parts.length - 1];
        if (lastPart.length === 1) {
          parts[parts.length - 1] = "0" + lastPart;
        }
        const limit = isMonthDate ? 2 : 3;
        if (parts.length < limit) {
          const formatted = parts.join("/") + "/";
          setDisplay(formatted);
        }
        return;
      }

      const digits = input.replace(/\D/g, "").slice(0, digitLimit);
      let formatted = digits;
      if (isMonthDate) {
        if (digits.length > 2) {
          formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
      } else {
        if (digits.length > 4) {
          formatted = `${digits.slice(0, 2)}/${digits.slice(
            2,
            4
          )}/${digits.slice(4)}`;
        } else if (digits.length > 2) {
          formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
      }
      setDisplay(formatted);

      const parsed = parseDisplay(formatted, isMonthDate);
      const hasBounds = bounds.min !== null || bounds.max !== null;
      const valid = parsed !== null && (!hasBounds || isDateInRange(parsed, bounds));
      onAnswer(parsed ? toISO(parsed, isMonthDate) : formatted, valid);
    },
    [onAnswer, isMonthDate, digitLimit, bounds]
  );

  return (
    <TextInput
      type="text"
      inputMode="numeric"
      value={display}
      onChange={handleChange}
      placeholder={isMonthDate ? t("MM/YYYY") : t("MM/DD/YYYY")}
      maxLength={maxLen}
      autoComplete="bday"
    />
  );
};

export function getDateErrorMessage(question: QuestionData): string {
  const isMonthDate = question.type === "month_date_input";
  const fmt = isMonthDate ? "MM/yyyy" : "MM/dd/yyyy";
  const bounds = getDateBounds(question);
  if (bounds.min && !bounds.max) {
    return `Please enter a date on or after ${format(bounds.min, fmt)}`;
  }
  if (!bounds.min && bounds.max) {
    return `Please enter a date on or before ${format(bounds.max, fmt)}`;
  }
  if (bounds.min && bounds.max) {
    return `Please enter a date between ${format(bounds.min, fmt)} and ${format(bounds.max, fmt)}`;
  }
  return "Please enter a valid date";
}

export default DateInputRenderer;
